/**
 * @fileoverview The building blocks of the Settings page: a titled section
 * holding one card of divided rows.
 *
 * Settings used to be thirteen stand-alone tiles, each with its own eyebrow,
 * padding and border — a page that scrolled for three screens to show a
 * profile, a plan and three switches. The shape here is the one the rest of
 * the studio uses (a SectionHeader over a Card of ListRow-style rows), so a
 * setting reads as a line in a group rather than a feature with a tile.
 */

import { ExternalLink } from "lucide-react";
import { Card, SectionHeader } from "@/components/studio/primitives";
import { cn } from "@/lib/utils";

/** A titled section: header + one card. Rows go inside. */
export function SettingsSection({ title, children, testId, className, id }) {
  return (
    <section id={id} className={className} data-testid={testId}>
      {/* A page that is only this section titles it itself. */}
      {title && <SectionHeader>{title}</SectionHeader>}
      <Card padding={0}>{children}</Card>
    </section>
  );
}

/**
 * One row of a section card. `title`/`desc` on the left, `trailing` on the
 * right, and `children` underneath the pair for rows that carry a control
 * or a meter of their own. Rows divide themselves; pass `last` on the final
 * one so the card's own border closes it.
 */
export function SettingsRow({ title, desc, trailing, children, last = false, align = "center", testId, className }) {
  return (
    <div
      data-testid={testId}
      className={cn("px-5 py-5 md:px-6", !last && "border-b border-[var(--studio-divider)]", className)}
    >
      {(title || desc || trailing) && (
        <div className={cn("flex justify-between gap-5", align === "start" ? "items-start" : "items-center")}>
          <div className="min-w-0 flex-1">
            {title && <div className="font-sans text-[15px] font-medium text-[var(--text-primary)]">{title}</div>}
            {desc && <div className="mt-1 font-sans text-[13px] leading-relaxed text-[var(--text-tertiary)]">{desc}</div>}
          </div>
          {trailing && <div className="shrink-0">{trailing}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

/** The on/off switch the notification and discoverability rows share. */
export function Toggle({ on, onChange, label, disabled = false, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onChange(!on)}
      className={cn(
        "relative shrink-0 rounded-full transition-colors disabled:opacity-50",
        on ? "bg-[var(--accent-yellow)]" : "bg-[var(--studio-interactive)]",
      )}
      style={{ minWidth: 40, height: 22 }}
    >
      <span
        className={cn(
          "absolute left-0.5 top-0.5 rounded-full bg-[var(--studio-knob)] shadow-sm transition-transform",
          on ? "translate-x-[18px]" : "translate-x-0",
        )}
        style={{ width: 18, height: 18 }}
      />
    </button>
  );
}

/** A row that is a link out — Academy, support, the policies. */
export function SettingsLinkRow({ href, label, sub, last = false, testId }) {
  const external = !href.startsWith("mailto:");
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel="noopener noreferrer"
      data-testid={testId}
      className={cn(
        "group flex items-center justify-between px-5 py-4 transition-colors hover:bg-[var(--studio-overlay)] md:px-6",
        !last && "border-b border-[var(--studio-divider)]",
      )}
    >
      <div>
        <p className="font-sans text-[15px] text-[var(--text-primary)]">{label}</p>
        {sub && <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">{sub}</p>}
      </div>
      <ExternalLink
        className="h-3.5 w-3.5 shrink-0 text-[var(--text-quaternary)] transition-colors group-hover:text-[var(--text-secondary)]"
        strokeWidth={1.5}
      />
    </a>
  );
}
